import {
  Directive,
  EventEmitter,
  OnInit,
  InputSignal,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Input,
  OnChanges,
  SimpleChanges,
} from "@angular/core";

export type DropDownOption = {
  id: string;
  name: string;
};

export type DropDownOptions = InputSignal<Array<DropDownOption>>;

export type DropDownTypes = string[] | DropDownOptions;

export const SORT_ALPHABET = "alphabet";
export const SORT_DATE = "date";
export type SortType = typeof SORT_ALPHABET | typeof SORT_DATE;

export interface DropdownSelectMapOption {
  realValue: string;
  optionDisplayValue: string;
  selectedDisplayValue: string;
  enabled?: boolean;
  /** Optional coloured text rendered after the option label (e.g. usage exclamation marks). */
  suffixText?: string;
  suffixClass?: string;
}

@Directive()
export class DropdownDirective implements OnInit, OnChanges, AfterViewInit {
  label: string = "";
  placeholder: string = "";
  options: DropDownTypes | DropdownSelectMapOption[] = [];
  mappedOptions: DropdownSelectMapOption[] = [];
  disabledOptions: string[] = [];
  selectedOption: string;
  optionSelected = new EventEmitter<string>();
  isExpanded = false;
  isTouched = false;
  /** todo: isRequired value should probably pass in through form validator.required, think of a way to support it */
  isRequired = false;

  divIsLessThan100px = false;

  @Input() sort: SortType = SORT_ALPHABET;

  @Input() set manualTouched(value: boolean) {
    if (value) {
      this.isTouched = true;
    }
  }

  @ViewChild("sizeDetector", { static: true }) sizeDetector: ElementRef;

  ngOnInit() {
    if (
      this.options instanceof Array &&
      !(
        this.options.length > 0 &&
        typeof this.options[0] === "object" &&
        "realValue" in this.options[0]
      )
    ) {
      this.options = this.sortOptions(this.options as string[], this.sort);
      // if option is disabled and the component is still showing the previous value, pass in the correct selected option
      const firstEnabledOption = this.options.find((option) => !this.isDisabled(option));
      // only emit when we auto-select a fallback; a pre-seeded selection is already known to the parent
      if (firstEnabledOption && !this.placeholder && !this.selectedOption) {
        this.selectedOption = firstEnabledOption;
        this.optionSelected.emit(this.selectedOption);
      }
    }
  }

  ngOnChanges(_changes: SimpleChanges) {
    this.mapOptions();
    this.resizeDiv();
  }

  ngAfterViewInit() {
    this.resizeDiv();
  }

  private resizeDiv() {
    if (this.sizeDetector) {
      // runtime error if no setTimeout, might have a better solution.
      setTimeout(() => {
        this.divIsLessThan100px = this.sizeDetector.nativeElement.offsetWidth < 100;
      });
    }
  }

  private sortOptions(options: string[], sortType: SortType = SORT_ALPHABET): string[] {
    if (sortType === SORT_DATE) {
      return options.slice();
    }
    return options.slice().sort((a, b) => a.localeCompare(b));
  }

  private sortMappedOptions(options: DropdownSelectMapOption[]): DropdownSelectMapOption[] {
    if (this.sort === SORT_DATE) {
      return options;
    }
    return options.slice().sort((a, b) => a.optionDisplayValue.localeCompare(b.optionDisplayValue));
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    this.markTouched();
  }

  onClickOutside() {
    if (this.isExpanded) {
      this.isExpanded = false;
    }
  }

  markTouched() {
    this.isTouched = true;
  }

  selectOption(option: string) {
    if (this.isDisabled(option)) {
      return;
    }
    this.selectedOption = option;
    this.optionSelected.emit(option);
    this.isExpanded = false;
  }

  getSelectedOptionDisplay() {
    const mappedSelectedOption = this.mappedOptions?.find(
      (opt) => opt.realValue === this.selectedOption,
    );
    if (mappedSelectedOption) {
      return mappedSelectedOption.selectedDisplayValue;
    }
    return this.selectedOption;
  }

  isDisabled(option: string): boolean {
    return this.disabledOptions.includes(option);
  }

  isInvalid(): boolean {
    return this.isRequired && this.isTouched && !this.selectedOption && !this.isExpanded;
  }

  mapOptions() {
    // check for type DropdownSelectMapOption[]
    if (
      Array.isArray(this.options) &&
      this.options.length > 0 &&
      !(typeof this.options[0] === "object" && "realValue" in this.options[0])
    ) {
      this.mappedOptions = this.sortOptions(this.options as string[], this.sort).map((opt) => ({
        realValue: opt,
        optionDisplayValue: opt,
        selectedDisplayValue: opt,
        enabled: true,
      }));
    } else if (Array.isArray(this.options)) {
      // Both option shapes sort themselves here on every ngOnChanges, so mappedOptions stays
      // correctly ordered even if this.options arrives fresh (e.g. a computed signal recalculating).
      this.mappedOptions = this.sortMappedOptions(this.options as DropdownSelectMapOption[]);
    } else {
      // this.options is a signal (e.g. DropdownMultiselectComponent shadows it with an
      // InputSignal) rather than a plain array — leave it as-is, this directive's sorting
      // doesn't apply to that shape.
      this.mappedOptions = this.options as unknown as DropdownSelectMapOption[];
    }
  }
}
